import Requests from "@/services/Requests";

const state = {
  cycles: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  allCycles: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  createStatus: "unitiliazed", // unitiliazed, loading, success, error
  updateStatus: "unitiliazed", // unitiliazed, loading, success, error
  deleteStatus: "unitiliazed", // unitiliazed, loading, success, error
  cycleById: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  changedLogs: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  cyclePermissions: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
};
const getters = {
  /**
   * Returns a subset of cycles based on the given page number.
   *
   * @param {Object} state - The Vuex state object.
   * @param {number} [page=0] - The page number to filter cycles by. Defaults to 0.
   * @return {Array} An array of cycles filtered by the given page number. If page is 0, returns all cycles.
   */
  getCycles:
    (state) =>
    (page = 0) =>
      page === 0
        ? state.cycles.data
        : state.cycles.data
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id)
            )
            .filter((d) => d.page === page),
  getAllCycles: (state) => state.allCycles.data,
  getCycleStatus: (state) => state.cycles.status,
  getCycleCount: (state) => state.cycles.count,
  getCyclePage: (state) => state.cycles.page,
  getCyclePages: (state) => Math.ceil(state.cycles.count / 10),
  getCyclesLoading: (state) => state.cycles.status === "loading",
  getCyclesStatus: (state) => state.cycles.status,
  getCycleById: (state) => (id) =>
    state.cycleById.data.find((d) => d.id === id) || {},
  getCycleChangedLogsById: (state) => (id) =>
    state.changedLogs.data.find((d) => d.cycleId === id) || { items: [] },
  getCycleChangedLogsLoadingById: (state) =>
    state.changedLogs.status === "loading",
  getCyclePermissionsById: (state) => (id) =>
    state.cyclePermissions.data.find((d) => d.cycleId === id) || {
      items: [],
    },
  getCyclePermissionsLoadingById: (state) =>
    state.cyclePermissions.status === "loading",
};
const mutations = {
  /**
   * Updates the status of the cycle in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the cycle.
   */
  SET_CYCLE_STATUS(state, payload) {
    state.cycles.status = payload;
  },
  /**
   * Sets the cycles and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of cycles.
   * @param {number} payload.count - The total count of cycles.
   */
  SET_CYCLES(state, payload) {
    state.cycles.data = payload.items;
    state.cycles.count = payload.count;
  },
  /**
   * Sets the cycles and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of cycles.
   * @param {number} payload.count - The total count of cycles.
   */
  SET_ALL_CYCLES(state, payload) {
    state.allCycles.data = payload.items;
    state.allCycles.count = payload.count;
  },
  /**
   * Updates the status of the update cycle in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the cycle.
   */
  SET_UPDATE_CYCLE_STATUS(state, payload) {
    state.updateStatus = payload;
  },
  /**
   * Updates an cycle object in the state with new information.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The updated cycle object.
   * @return {void}
   */
  SET_UPDATE_CYCLE(state, payload) {
    const index = state.cycles.data.findIndex((d) => d.id === payload.id);
    const cycle = state.cycles.data[index];
    state.cycles.data[index] = { ...cycle, ...payload };
  },
  SET_CREATE_CYCLE_STATUS(state, payload) {
    state.createStatus = payload;
  },
  /**
   * Adds a new cycle to the list of cycles in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The cycle object to be added.
   * @return {void}
   */
  SET_CREATE_CYCLE(state, payload) {
    state.cycles.data.push(payload);
  },
  /**
   * Updates the status of the delete cycle in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the cycle.
   */
  SET_DELETE_CYCLE_STATUS(state, payload) {
    state.deleteStatus = payload;
  },
  /**
   * Removes an cycle from the state based on the provided payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the cycle ID to be deleted.
   * @param {number} payload.id - The ID of the cycle to be deleted.
   * @return {void}
   */
  SET_DELETE_CYCLE(state, payload) {
    state.cycles.data = state.cycles.data.filter((d) => d.id !== payload.id);
  },
  SET_CYCLE_BY_ID_STATUS(state, payload) {
    state.cycleById.status = payload;
  },
  SET_CYCLE_BY_ID(state, payload) {
    state.cycleById.data.push(payload);
  },
  SET_CYCLE_CHANGED_LOGS_BY_ID_STATUS(state, payload) {
    state.changedLogs.status = payload;
  },
  SET_CYCLE_CHANGED_LOGS_BY_ID(state, payload) {
    const index = state.changedLogs.data.findIndex(
      (d) => d.cycleId === payload.cycleId
    );
    if (index > -1) state.changedLogs.data.splice(index, 1);
    state.changedLogs.data.unshift(payload);
  },
  SET_CYCLE_PERMISSIONS_BY_ID_STATUS(state, payload) {
    state.cyclePermissions.status = payload;
  },
  SET_CYCLE_PERMISSIONS_BY_ID(state, payload) {
    const index = state.cyclePermissions.data.findIndex(
      (d) => d.cycleId === payload.cycleId
    );
    if (index > -1) state.cyclePermissions.data.splice(index, 1);
    state.cyclePermissions.data.unshift(payload);
  },
};

const actions = {
  /**
   * Retrieves a list of cycles based on the provided payload.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the query parameters.
   * @param {Object} payload.query - The query parameters.
   * @param {string} payload.query.paymentMethodId - The ID of the payment method to filter by.
   * @return {Promise<void>}
   */
  async getCycles({ commit, state }, payload) {
    const status = state.cycles.status;
    commit("SET_CYCLE_STATUS", "loading");
    const response = await Requests.Cycle.GetAll(payload);
    console.log(response, " ommggg");
    if (response.message === "OK") {
      commit("SET_CYCLES", {
        items: response.data.items.map((item) => ({
          ...item,
          page: payload.query.page || 1,
        })),
        count: response.data.count,
      });
      if (status === "unitiliazed")
        commit("SET_ALL_CYCLES", {
          items: response.data.items.map((item) => ({
            ...item,
            page: payload.query.page || 1,
          })),
          count: response.data.count,
        });
    }
    commit(
      "SET_CYCLE_STATUS",
      response.message === "OK" ? "success" : response.message
    );
    return response.message;
  },
  /**
   * Creates an cycle by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the cycle data.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.cycleRate - The cycle rate.
   * @return {Promise<void>} - A promise that resolves when the creation is complete.
   */
  async createCycle({ state, commit }, payload) {
    state.createStatus = "loading";
    try {
      const response = await Requests.Cycle.Create(payload);
      state.createStatus = "success";
      if (response.message === "OK")
        commit("SET_CREATE_CYCLE", {
          id: response.data,
          paymentMethodId: payload.paymentMethodId,
          cycleRate: payload.cycleRate,
          isActive: true,
          page: 1,
        });
      else state.createStatus = "error";
    } catch {
      state.createStatus = "error";
    }
  },
  /**
   * Updates an cycle by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the cycle data to update.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.cycleRate - The cycle rate.
   * @param {boolean} payload.isActive - Whether the cycle is active or not.
   * @return {Promise<void>} - A promise that resolves when the update is complete.
   */
  async updateCycle({ state, commit }, payload) {
    state.updateStatus = "loading";
    try {
      const response = await Requests.Cycle.Update(payload, {
        params: { id: payload.id },
      });
      if (response.message === "OK") {
        commit("SET_UPDATE_CYCLE", payload);
        state.updateStatus = "success";
      } else {
        state.updateStatus = "error";
      }
    } catch {
      state.updateStatus = "error";
    }
  },
  /**
   * Removes an cycle from the state and updates the delete status.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the cycle data.
   * @param {number} payload.id - The ID of the cycle to be removed.
   * @returns {Promise<void>} A promise that resolves when the cycle is successfully removed.
   */
  removeCycle({ state }, { id }) {
    state.deleteStatus = "loading";

    return Requests.Cycle.Delete({ params: { id } })
      .then((response) => {
        state.deleteStatus = response.message === "OK" ? "success" : "error";
        return response;
      })
      .catch(() => {
        state.deleteStatus = "error";
        return { message: "ERROR" };
      });
  },
  /**
   * Retrieves an cycle by their ID and updates the cycle by ID status in the Vuex store.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} id - The ID of the cycle to retrieve.
   * @return {Promise} A promise that resolves when the cycle is successfully retrieved and the status is updated.
   */
  getCycleById({ commit, state }, { id }) {
    // check if cycle already exists in the state;
    if (state.cycleById.data.find((cycle) => cycle.id === id))
      return {
        message: "OK",
        data: state.cycleById.data.find((cycle) => cycle.id === id),
      };
    commit("SET_CYCLE_BY_ID_STATUS", "loading");
    return Requests.Cycle.GetById({ params: { id } })
      .then((response) => {
        commit("SET_CYCLE_BY_ID", response.data);
        commit("SET_CYCLE_BY_ID_STATUS", "success");
        return response;
      })
      .catch(() => {
        commit("SET_CYCLE_BY_ID_STATUS", "error");
        return { message: "ERROR" };
      });
  },
  getCycleChangedLogsById({ commit, state }, { cycleId, force = false }) {
    // check if battery already exists in the state;
    console.log(cycleId, "cycleId");

    if (
      state.changedLogs.data.find((cycle) => cycle.cycleId === cycleId) &&
      !force
    )
      return state.changedLogs.data.find((cycle) => cycle.cycleId === cycleId);
    commit("SET_CYCLE_CHANGED_LOGS_BY_ID_STATUS", "loading");
    return Requests.Cycle.GetCycleLogs({ query: { id: cycleId } })
      .then((response) => {
        commit("SET_CYCLE_CHANGED_LOGS_BY_ID", {
          cycleId,
          ...response.data,
        });
        commit("SET_CYCLE_CHANGED_LOGS_BY_ID_STATUS", "success");
        return response.data;
      })
      .catch(() => {
        commit("SET_CYCLE_CHANGED_LOGS_BY_ID_STATUS", "error");
        return {};
      });
  },
  getCyclePermissionsById({ commit, state }, { cycleId, force = false }) {
    // check if battery already exists in the state;

    if (
      state.cyclePermissions.data.find((cycle) => cycle.cycleId === cycleId) &&
      !force
    )
      return state.cyclePermissions.data.find(
        (cycle) => cycle.cycleId === cycleId
      );
    commit("SET_CYCLE_PERMISSIONS_BY_ID_STATUS", "loading");
    return Requests.CyclePermissions.GetAll({
      query: { cycleId: cycleId },
    })
      .then((response) => {
        commit("SET_CYCLE_PERMISSIONS_BY_ID", {
          cycleId,
          ...response.data,
        });
        commit("SET_CYCLE_PERMISSIONS_BY_ID_STATUS", "success");
        return response.data;
      })
      .catch(() => {
        commit("SET_CYCLE_PERMISSIONS_BY_ID_STATUS", "error");
        return {};
      });
  },
};

export default { state, getters, mutations, actions };
